import {
  appointmentModeValueMapping,
  SlotAvailabilityType,
} from 'app/Bookings/constants'
import { cn } from '@/lib/utils'
import ControlledPillMultiselectFilter from 'common/ControlledPillMultiselectFilter'
import { DayOfWeek, TimeOfDay } from 'constants/common'
import { summarizeProviderFilterSelection } from 'constants/profileDayOfWeekFilterOptions'
import useBreakpoint from 'hooks/useBreakPoint'
import { useMemo, useState } from 'react'

const MODAL_FILTER_DROPDOWN_Z = 'zindex-popover !~z-[1205] ~shadow-lg'

type ModalAvailabilityFilterPillsProps = {
  visitTypeOptions?: ProviderFilter[]
  visitTypeSelectedIds?: string[]
  setVisitTypeSelectedIds?: (next: string[]) => void
  availabilityLabels?: SlotAvailabilityType[]
  providerFirstName?: string
  timeOfDay: TimeOfDay[]
  setTimeOfDay: (next: TimeOfDay[]) => void
  timeOfDayFilters?: ProviderFilter[]
  dayOfWeek: DayOfWeek[]
  setDayOfWeek: (next: DayOfWeek[]) => void
  dayOfWeekFilters?: ProviderFilter[]
  idPrefix: string
  className?: string
  /** Profile-mobile treatment: horizontal-scrolling pill row + design typography on the copy. */
  isProfileMobile?: boolean
  /** Set to false to disable the visit type pill, e.g. while a booking confirmation is pending. */
  isEnabled?: boolean
}

const ModalAvailabilityFilterPills: React.FC<ModalAvailabilityFilterPillsProps> = ({
  visitTypeOptions,
  visitTypeSelectedIds,
  setVisitTypeSelectedIds,
  availabilityLabels,
  providerFirstName,
  timeOfDay,
  setTimeOfDay,
  timeOfDayFilters,
  dayOfWeek,
  setDayOfWeek,
  dayOfWeekFilters,
  idPrefix,
  className,
  isProfileMobile = false,
  isEnabled = true,
}) => {
  const { isAboveXLSize } = useBreakpoint()

  const [visitTypeOpen, setVisitTypeOpen] = useState(false)
  const [todOpen, setTodOpen] = useState(false)
  const [dowOpen, setDowOpen] = useState(false)

  const modalDropdownProps = {
    // Profile mobile: the pill row scrolls horizontally (overflow container), so the menu
    // must be fixed/portal to escape the clip. Elsewhere: absolute, so menus stay with the
    // pills while the modal scrolls.
    useFixedDropdown: isProfileMobile,
    dropdownMenuSize: 'compact' as const,
    dropdownSurfaceClassName: MODAL_FILTER_DROPDOWN_Z,
  }

  const visitTypeSummary = useMemo(
    () =>
      summarizeProviderFilterSelection(
        visitTypeSelectedIds ?? [],
        visitTypeOptions ?? []
      ),
    [visitTypeSelectedIds, visitTypeOptions]
  )

  const todSummary = useMemo(
    () =>
      (timeOfDayFilters || [])
        .filter((f) => timeOfDay.includes(f.id as TimeOfDay))
        .map((f) => f.value)
        .join(', '),
    [timeOfDay, timeOfDayFilters]
  )

  const dowSummary = useMemo(
    () =>
      (dayOfWeekFilters || [])
        .filter((f) => dayOfWeek.includes(f.id as DayOfWeek))
        .map((f) => f.value)
        .join(', '),
    [dayOfWeek, dayOfWeekFilters]
  )

  const hasVisitTypeSupport =
    !!setVisitTypeSelectedIds && visitTypeOptions != null

  const showVisitType =
    hasVisitTypeSupport && (visitTypeOptions?.length ?? 0) > 1
  const showSingleVisitTypeCopy = (availabilityLabels?.length ?? 0) === 1
  const showTod = !!timeOfDayFilters?.length
  const showDow = !!dayOfWeekFilters?.length

  if (!showVisitType && !showSingleVisitTypeCopy && !showTod && !showDow) {
    return null
  }

  const singleVisitTypeLabel =
    showSingleVisitTypeCopy && availabilityLabels && availabilityLabels[0]
      ? availabilityLabels[0] === SlotAvailabilityType.ONLINE
        ? appointmentModeValueMapping['Online'].split(' ')[0]
        : appointmentModeValueMapping['In Person']
      : ''

  return (
    <div className={cn('~mx-[6%] ~mb-4 max-[629px]:~mx-[2%]', className)}>
      {showSingleVisitTypeCopy && (
        <p
          className={cn(
            '~mb-3 ~font-gibson ~text-charcoal',
            // Profile mobile: Body/DEFAULT (16px/24px, 400); legacy elsewhere (14px).
            isProfileMobile ? '~text-base ~font-normal' : '~text-sm'
          )}
        >
          {providerFirstName} is available for {singleVisitTypeLabel} visits.
        </p>
      )}
      <div
        className={cn(
          '~flex ~items-center ~gap-2',
          isProfileMobile
            ? '~flex-nowrap ~overflow-x-auto hide-scrollbar [&>*]:~shrink-0'
            : '~flex-wrap'
        )}
      >
        {showVisitType && visitTypeOptions && setVisitTypeSelectedIds && (
          <ControlledPillMultiselectFilter
            options={visitTypeOptions}
            selectedIds={visitTypeSelectedIds ?? []}
            onSelectionChange={setVisitTypeSelectedIds}
            placeholder="Visit type"
            pillSummaryText={visitTypeSummary}
            isOpen={visitTypeOpen}
            onOpenChange={setVisitTypeOpen}
            listItemIdPrefix={`${idPrefix}-visit`}
            disabled={!isEnabled}
            {...modalDropdownProps}
          />
        )}
        {showDow && (
          <ControlledPillMultiselectFilter
            options={dayOfWeekFilters}
            selectedIds={dayOfWeek}
            onSelectionChange={(ids) => setDayOfWeek(ids as DayOfWeek[])}
            placeholder="Day of the week"
            pillSummaryText={dowSummary}
            isOpen={dowOpen}
            onOpenChange={setDowOpen}
            listItemIdPrefix={`${idPrefix}-dow`}
            dropdownAlign={isAboveXLSize ? 'start' : 'auto'}
            {...modalDropdownProps}
          />
        )}
        {showTod && (
          <ControlledPillMultiselectFilter
            options={timeOfDayFilters}
            selectedIds={timeOfDay}
            onSelectionChange={(ids) => setTimeOfDay(ids as TimeOfDay[])}
            placeholder="Time of day"
            pillSummaryText={todSummary}
            isOpen={todOpen}
            onOpenChange={setTodOpen}
            listItemIdPrefix={`${idPrefix}-tod`}
            {...modalDropdownProps}
          />
        )}
      </div>
    </div>
  )
}

export default ModalAvailabilityFilterPills
